import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Daily digest email signup — footer widget. Reuses Resend on the backend.
 * Region is pulled from localStorage so the visitor gets the edition they've
 * already been browsing.
 */
export default function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success

  async function onSubmit(e) {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Enter a valid email address");
      return;
    }
    setStatus("sending");
    let region = "US";
    try {
      const r = localStorage.getItem("tc_region");
      if (r && /^[A-Za-z]{2}$/.test(r)) region = r.toUpperCase();
    } catch { /* SSR */ }

    try {
      const res = await api.post("/newsletter/subscribe", { email: trimmed, region });
      setStatus("success");
      toast.success(res.data.message || "Subscribed!");
      setEmail("");
    } catch (err) {
      setStatus("idle");
      toast.error(err?.response?.data?.detail || "Could not subscribe — try again.");
    }
  }

  if (status === "success") {
    return (
      <section className="mt-16 border-t-2 border-ink pt-8" data-testid="newsletter-signup-success">
        <div className="max-w-2xl mx-auto text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-press-red mb-3">
            ✓ Subscribed
          </div>
          <h3 className="font-newspaper text-2xl sm:text-3xl text-ink font-bold mb-2">
            Check your inbox for the welcome note
          </h3>
          <p className="text-sm text-ink-muted">
            Your first daily digest arrives tomorrow morning.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-16 border-t-2 border-ink pt-8" data-testid="newsletter-signup">
      <div className="max-w-2xl mx-auto text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-press-red mb-3">
          ✒ The daily edition
        </div>
        <h3 className="font-newspaper text-2xl sm:text-3xl text-ink font-bold mb-2">
          One newspaper a day, from your inbox
        </h3>
        <p className="text-sm text-ink-muted mb-5 max-w-md mx-auto">
          Each morning, we deliver the front page from today's date in history —
          calibrated for your region. Free. Unsubscribe anytime.
        </p>
        <form
          onSubmit={onSubmit}
          className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
          data-testid="newsletter-signup-form"
        >
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="flex-1 bg-paper-warm border border-ink px-4 py-3 font-mono text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-press-red"
            data-testid="newsletter-signup-email"
          />
          <button
            type="submit"
            disabled={status === "sending"}
            className="bg-press-red text-paper-warm font-mono uppercase tracking-widest text-xs px-6 py-3 border border-ink hover:opacity-90 disabled:opacity-50"
            data-testid="newsletter-signup-submit"
          >
            {status === "sending" ? "…" : "Subscribe"}
          </button>
        </form>
        <p className="text-[10px] uppercase tracking-widest text-ink-muted mt-3">
          One email per day · unsubscribe in one click
        </p>
      </div>
    </section>
  );
}
